import Layout from "../utilities/Layout";
import Grid from "@mui/material/Grid";
import Toolbar from "@mui/material/Toolbar";
import * as React from "react";
import {useEffect, useState} from "react";
import Button from "@mui/material/Button";
import PickBracketContainerTopMoments from "../components/bracket/picks/two_wide/PickBracketContainerTopMoments";
import Box from "@mui/material/Box";
import {Event} from "../data/models/bracket_api/Event";
import {Pick} from "../data/models/bracket_api/Pick";
import {Bracket} from "../data/models/bracket_api/Bracket";
import {Result} from "../data/models/bracket_api/Result";
import {Qualifier} from "../data/models/bracket_api/Qualifier";
import {bracket_screen_load, top_moments_bracket_screen_load} from "../apis/ScreenLoad";
import ShuffleIcon from '@mui/icons-material/Shuffle';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import IosShareIcon from '@mui/icons-material/IosShare';
import PublishIcon from '@mui/icons-material/Publish';
import {RaceClass, raceClassFromInt} from "../data/models/bracket_api/RaceClass";
import {Driver} from "../data/models/bracket_api/Driver";
import {
    generateBlankPicks,
    randomizePicksTopMoments
} from "../utilities/bracket_tools/PickGeneration";
import {User} from "../data/models/user_auth/User";
import {PickStatus, pickStatusFromInt} from "../data/models/bracket_api/PickStatus";
import Typography from "@mui/material/Typography";
import {League} from "../data/models/user_auth/League";
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Snackbar,
    Theme,
    useMediaQuery
} from "@mui/material";
import {PickResponse, submitPicks, submitPicksTopMoments} from "../apis/Pick";
import CssBaseline from "@mui/material/CssBaseline";
import {Helmet} from "react-helmet";
import {FacebookShareButton, TwitterShareButton} from "react-share";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import GA4EventSend from "../utilities/GA4EventSend";
import Loader from "../components/common/Loader";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import IconButton from "@mui/material/IconButton";


export type BracketTopMomentsScreenProps = {
    event: Event,
    brackets: Bracket[],
    picks: Pick[],
    results: Result[],
    qualifiers: Qualifier[],
}

export default function BracketTopMomentsScreen() {
    const [props, setProps] = useState<BracketTopMomentsScreenProps | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    let searchParams = window.location.href.substring(window.location.href.indexOf("?") + 1);
    let decoded = decodeURIComponent(searchParams);
    const queryParams = new URLSearchParams(decoded);
    const [eventId, setEventId] = useState(queryParams.get('event_id'));
    const leagueId = queryParams.get('league_id') || "1";
    let userId = queryParams.get('user_id');
    const [nickname, setNickname] = useState<string>("");
    const [isClicked, setIsClicked] = useState<boolean>(false);
    let user: User | undefined = JSON.parse(localStorage.getItem('user') ?? "{}") as User || undefined;
    if(userId === null){
        try {
            userId = user.id.toString();
        }
        catch (e) {
            let pathname = encodeURIComponent("/bracket/top-moments?event_id=" + eventId + "&league_id=" + leagueId);
            window.location.href = "/signin/redirect?path=" + pathname;
        }
    }
    let [league, setLeague] = useState<League | undefined>(undefined);
    const [ownPicks, setOwnPicks] = useState<boolean>(user?.id === parseInt(userId ?? "0") || user?.nickname === userId);
    const [resetOpen, setResetOpen] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);
    const [changed, setChanged] = useState<"up-to-date" | "changed" | "sending">("up-to-date");
    const [open, setOpen] = useState<boolean>(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [message, setMessage] = useState<string>("");
    const [severity, setSeverity] = useState<"success" | "info" | "warning" | "error" | undefined>("error");
    const [isMdOrUp, setIsMdOrUp] = useState(useMediaQuery((theme: Theme) => theme.breakpoints.up('md')));

    const handleHelpClose = () => {
        setHelpOpen(false);
    }

    const handleResetClickOpen = () => {
        setResetOpen(true);
    };
    const handleResetClose = () => {
        setResetOpen(false);
    };
    const handleShareClickOpen = () => {
        setShareOpen(true);
    };
    const handleShareClose = () => {
        setShareOpen(false);
    };

    // Top Moments brackets are only available for the main league (ID 1)
    useEffect(() => {
        if (parseInt(leagueId) !== 1) {
            window.location.href = "/leagues/";
        }
    }, [leagueId]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (changed === "changed") {
                handleSubmitPicks(false);
            }
        }, 5000); // 5000 milliseconds = 5 seconds
        if (loading) {
            top_moments_bracket_screen_load(leagueId ?? '', userId ?? "0", eventId ?? "").then((response) => {
                if (response.succeeded) {
                    GA4EventSend('bracket', 'load', 'bracket', 200, false)
                    setNickname(response.data.nickname);
                    let drivers: Driver[] = response.data.drivers;
                    drivers.forEach((driver: any) => {
                        let newDivisions: RaceClass[] = [];
                        for (let division of driver.divisions) {
                            newDivisions.push(raceClassFromInt(division));
                        }
                        driver.divisions = newDivisions;
                    })
                    let event = response.data.event as Event;
                    if(eventId === null){
                        setEventId(event.id as string);
                    }
                    response.data.brackets.forEach((bracket: any) => {
                        bracket.race_class = raceClassFromInt(bracket.race_class);
                    });
                    let brackets: Bracket[] = response.data.brackets;
                    response.data.qualifiers.forEach((qualifier: any) => {
                        qualifier.event = event;
                        qualifier.bracket = response.data.brackets.find((bracket: Bracket) => {
                            return bracket.id === qualifier.bracket_id
                        });
                        qualifier.driver = drivers.find((driver: any) => {
                            return driver.id === qualifier.driver_id
                        });
                        qualifier.race_class = raceClassFromInt(qualifier.race_class)
                        delete qualifier.bracket_id;
                        delete qualifier.driver_id;
                        delete qualifier.event_id;
                    });
                    let qualifiers: Qualifier[] = response.data.qualifiers;
                    // Top Moments is always two-wide
                    response.data.results.forEach((result: any) => {
                        result.bracket = brackets.find((bracket: Bracket) => {
                            return bracket.id === result.bracket_id
                        });
                        delete result.bracket_id;
                        result.winner = drivers.find((driver: any) => {
                            return driver.id === result.winner_id
                        });
                        delete result.winner_id;
                        result.loser = drivers.find((driver: any) => {
                            return driver.id === result.loser_id
                        });
                        delete result.loser_id;
                        result.competitor_1 = drivers.find((driver: any) => {
                            return driver.id === result.competitor_1_id
                        });
                        delete result.competitor_1_id;
                        result.competitor_2 = drivers.find((driver: any) => {
                            return driver.id === result.competitor_2_id
                        });
                        delete result.competitor_2_id;
                    });
                    let results: Result[] = response.data.results;
                    let picks: Pick[] = response.data.picks;
                    setLeague(response.data.league);
                    if(user !== undefined && user.id === parseInt(userId ?? "0") && (response.data.picks === undefined || response.data.picks.length === 0)){
                        if(leagueId !== null && leagueId === "1"){
                            setHelpOpen(true);
                        }
                        picks = generateBlankPicks({
                            results: results,
                            isFourWide: false,
                            player: user,
                            league: response.data.league,
                        }) as Pick[]
                    } else {
                        let rawPicks = response.data.picks;
                        let cleanedPicks: Pick[] = [];
                        rawPicks.forEach((pick: any) => {
                            cleanedPicks.push({
                                id: pick.id,
                                player: user,
                                competitor_1: drivers.find((driver: any) => {
                                    return driver.id === pick.competitor_1_id
                                }),
                                competitor_2: drivers.find((driver: any) => {
                                    return driver.id === pick.competitor_2_id
                                }),
                                seed_1: pick.seed_1,
                                seed_2: pick.seed_2,
                                winner: drivers.find((driver: any) => {
                                    return driver.id === pick.winner_id
                                }),
                                winner_seed: pick.winner_seed,
                                loser: drivers.find((driver: any) => {
                                    return driver.id === pick.loser_id
                                }),
                                loser_seed: pick.loser_seed,
                                league: response.data.league,
                                result: results.find((result: any) => {
                                    return result.game_code === pick.result_id
                                }),
                                pick_status: pickStatusFromInt(pick.pick_status),
                                possible_points: pick.possible_points,
                            } as Pick)
                        })
                        picks = cleanedPicks;
                    }
                    setProps({
                        event: event,
                        brackets: brackets,
                        qualifiers: qualifiers,
                        picks: picks,
                        results: results,
                    });
                    setLoading(false);
                } else {
                    GA4EventSend('bracket', 'load', 'bracket', 500, false)
                    setLoading(false);
                }
            });
        }
        const handleResize = () => {
            setIsMdOrUp(window.innerWidth >= 960);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            clearInterval(interval);
        };
    }, [loading, ownPicks]);

    function updatePickTwoWide(pick: Pick) {
        let picks = props?.picks as Pick[];
        if (pick.pick_status === PickStatus.PENDING) {
            // Winner does not exist make sure no one from this pick is in any future picks
            if (pick.winner !== undefined) {
                // Ensure winner_seed is set correctly from the competitor's seed
                if (pick.winner_seed === undefined) {
                    if (pick.competitor_1 === pick.winner) {
                        pick.winner_seed = pick.seed_1;
                    } else if (pick.competitor_2 === pick.winner) {
                        pick.winner_seed = pick.seed_2;
                    }
                }
                let game_code = pick.result.game_code;
                let nextPick = picks.find((pick2: Pick) => {
                    return pick2.pick_status !== PickStatus.TIEBREAKER && (pick2.result.competitor_1_game_code === game_code || pick2.result.competitor_2_game_code === game_code);
                });
                if (nextPick !== undefined) {
                    if (nextPick.result.competitor_1_game_code === game_code) {
                        nextPick.competitor_1 = pick.winner;
                        nextPick.seed_1 = pick.winner_seed;
                    } else if (nextPick.result.competitor_2_game_code === game_code) {
                        nextPick.competitor_2 = pick.winner;
                        nextPick.seed_2 = pick.winner_seed;
                    }
                }
                let round = pick.result.round;
                let invalidPicks = picks.filter((pick2: Pick) => {
                    return pick2.result.round > round &&
                        (pick2.competitor_1 === pick.loser ||
                            pick2.competitor_2 === pick.loser
                        );
                });
                for (let invalidPick of invalidPicks) {
                    if (invalidPick.competitor_1 === pick.loser) {
                        invalidPick.competitor_1 = undefined;
                        invalidPick.seed_1 = undefined;
                    } else if (invalidPick.competitor_2 === pick.loser) {
                        invalidPick.competitor_2 = undefined;
                        invalidPick.seed_2 = undefined;
                    }
                }
                for (let invalidPick of invalidPicks) {
                    picks.forEach((pick2: Pick) => {
                        if (pick2.id === invalidPick.id) {
                            pick2 = invalidPick;
                        }
                    })
                }
                picks.forEach((pick2: Pick) => {
                    if (pick2.id === nextPick?.id) {
                        pick2 = nextPick;
                    } else if (pick2.id === pick.id) {
                        pick2 = pick;
                    }
                })
            }
            // Winner exists make sure loser from this pick is not in any future picks
            else {
                let round = pick.result.round;
                let invalidPicks = picks.filter((pick2: Pick) => {
                    return pick2.result.round > round &&
                        (pick2.competitor_1 === pick.competitor_1 ||
                            pick2.competitor_1 === pick.competitor_2 ||
                            pick2.competitor_2 === pick.competitor_1 ||
                            pick2.competitor_2 === pick.competitor_2
                        );
                });
                for (let invalidPick of invalidPicks) {
                    if (invalidPick.competitor_1 === pick.competitor_1 || invalidPick.competitor_1 === pick.competitor_2) {
                        if (invalidPick.competitor_1 === invalidPick.winner) {
                            invalidPick.winner = undefined;
                            invalidPick.winner_seed = undefined;
                            invalidPick.loser = undefined;
                            invalidPick.loser_seed = undefined;
                        }
                        invalidPick.competitor_1 = undefined;
                        invalidPick.seed_1 = undefined;
                    }
                    if (invalidPick.competitor_2 === pick.competitor_1 || invalidPick.competitor_2 === pick.competitor_2) {
                        if (invalidPick.competitor_2 === invalidPick.winner) {
                            invalidPick.winner = undefined;
                            invalidPick.winner_seed = undefined;
                            invalidPick.loser = undefined;
                            invalidPick.loser_seed = undefined;
                        }
                        invalidPick.competitor_2 = undefined;
                        invalidPick.seed_2 = undefined;
                    }
                }
                for (let invalidPick of invalidPicks) {
                    picks.forEach((pick2: Pick) => {
                        if (pick2.id === invalidPick.id) {
                            pick2 = invalidPick;
                        }
                    })
                }
                picks.forEach((pick2: Pick) => {
                    if (pick2.id === pick.id) {
                        pick2 = pick;
                    }
                })
            }
        } else {
            return;
        }
        // Update seeds from results (for Top Moments, results have quadrant-based seeds)
        // Only update seeds if they're not already set (preserve seeds from previous winners)
        picks.forEach((pick2: Pick) => {
            const result = props?.results?.find((r) => r.game_code === pick2.result.game_code);
            if(pick2.competitor_1){
                // Preserve existing seed if set, otherwise use result seed or look up from qualifiers
                if(pick2.seed_1 === undefined){
                    pick2.seed_1 = result?.seed_1 ?? props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_1})?.seed;
                }
            }
            else{
                pick2.seed_1 = undefined;
            }
            if(pick2.competitor_2){
                // Preserve existing seed if set, otherwise use result seed or look up from qualifiers
                if(pick2.seed_2 === undefined){
                    pick2.seed_2 = result?.seed_2 ?? props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_2})?.seed;
                }
            }
            else{
                pick2.seed_2 = undefined;
            }
            if(pick2.winner){
                // Winner seed should match the seed of the competitor that won
                if(pick2.winner_seed === undefined){
                    if(pick2.competitor_1 === pick2.winner){
                        pick2.winner_seed = pick2.seed_1;
                    } else if(pick2.competitor_2 === pick2.winner){
                        pick2.winner_seed = pick2.seed_2;
                    } else {
                        pick2.winner_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.winner})?.seed;
                    }
                }
            }
            else{
                pick2.winner_seed = undefined;
            }
            if(pick2.loser){
                pick2.loser_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.loser})?.seed;
            }
            else{
                pick2.loser_seed = undefined;
            }
        });
        // set props
        if (props !== undefined) {
            setProps({
                event: props.event,
                picks: picks,
                brackets: props.brackets,
                results: props.results,
                qualifiers: props.qualifiers,
            });
        }
    }

    function calculatePossiblePointsTwoWide(pick: any) {
        let possible_points = 0;
        if(pick.pick_status !== 4) {
            if (pick.seed_1 && pick.seed_2 && pick.winner_seed) {
                possible_points = Math.pow(2, pick.result.round - 1);
                if ((pick.seed_1 - pick.seed_2 >= 7 && pick.seed_1 === pick.winner_seed) ||
                    (pick.seed_2 - pick.seed_1 >= 7 && pick.seed_2 === pick.winner_seed)) {
                    possible_points *= 2;
                }
            }
            possible_points *= props?.brackets[0]?.points_modifier ?? 1;
        }
        return possible_points;
    }

    function cleanPicksForSave() {
        let picksCopy: any = (props?.picks as Pick[]).map((pick: Pick) => ({...pick}));
        picksCopy.forEach((pick: any) => {
            pick.pick_status = pick.pick_status + 1;
            pick.possible_points = calculatePossiblePointsTwoWide(pick);
            pick.result_id = pick.result.game_code;
            delete pick.result;
            pick.league_id = leagueId;
            delete pick.league
            pick.competitor_1_id = pick.competitor_1?.id ?? null;
            delete pick.competitor_1;
            pick.competitor_2_id = pick.competitor_2?.id ?? null;
            delete pick.competitor_2;
            pick.winner_id = pick.winner?.id ?? null;
            delete pick.winner;
            pick.winner_seed = pick.winner_seed ?? null;
            pick.loser_seed = pick.loser_seed ?? null;
            pick.seed_1 = pick.seed_1 ?? null;
            pick.seed_2 = pick.seed_2 ?? null;
            pick.loser_id = pick.loser?.id ?? null;
            delete pick.loser;
            pick.player_id = userId;
            delete pick.player;
            if (pick.winner_id !== null && pick.loser_id === null) {
                if (pick.competitor_1_id !== pick.winner_id) {
                    pick.loser_id = pick.competitor_1_id;
                } else {
                    pick.loser_id = pick.competitor_2_id;
                }
            }
        });
        return picksCopy;
    }

    function handleAutoSave(response: PickResponse) {
        if (response.succeeded) {
            if (changed !== "changed") {
                setChanged("up-to-date")
            }
        }
    }

    function getShareLink() {
        return "https://dragracebracketbonanza.com/bracket/top-moments/?" + encodeURIComponent("event_id=" + eventId + "&league_id=" + leagueId + "&user_id=" + userId);
    }

    async function handleSubmitPicks(submit: boolean) {
        setChanged("sending");
        let picksCopy = cleanPicksForSave();
        let response = await submitPicksTopMoments(localStorage.getItem('access_token') ?? "", localStorage.getItem('refresh_token') ?? "", picksCopy)
        if (submit) {
            if (response.succeeded) {
                setMessage("Picks Submitted Successfully. Returning to Leagues Page.");
                setSeverity("success");
                setOpen(true);
                setTimeout(() => {
                    window.location.href = "/leagues/";
                }, 3000);
            } else {
                if(response.message) {
                    setMessage(response.message);
                }
                else{
                    setMessage("Failed to submit picks, please refresh the page and try again.")
                }
                setSeverity("error");
                setOpen(true);
            }
        }
        GA4EventSend('bracket', 'submit', 'bracket', response.statusCode, false)
        handleAutoSave(response);
    }

    function updatePick(pick: Pick) {
        updatePickTwoWide(pick);
        setChanged("changed")
    }

    return (
        <>
            <Helmet>
                <title>DRBB | Top Moments Bracket</title>
            </Helmet>
            <Layout>
                <CssBaseline/>
                <Snackbar anchorOrigin={{vertical: "top", horizontal: "center"}} open={open} onClose={() => {
                    setOpen(false)
                }}
                          autoHideDuration={6000}>
                    <Alert elevation={6} severity={severity} sx={{width: '100%'}} variant="filled">
                        {message}
                    </Alert>
                </Snackbar>
                <Dialog
                    open={resetOpen}
                    onClose={handleResetClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Reset Picks"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Are you sure you want to reset your picks?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleResetClose} color="primary">
                            Cancel
                        </Button>
                        <Button onClick={() => {
                            if (props?.event && user) {
                                setProps({
                                    event: props.event,
                                    picks: generateBlankPicks({
                                        isFourWide: false,
                                        player: user,
                                        league: props.picks[0].league,
                                        results: props.results,
                                    }) as Pick[],
                                    brackets: props.brackets,
                                    results: props.results,
                                    qualifiers: props.qualifiers,
                                });
                                setChanged("changed");
                            }
                            handleResetClose();
                        }} color="primary" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={helpOpen}
                    onClose={handleHelpClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Welcome to the Top Moments bracket! This is a 64-entry bracket featuring the greatest moments in drag racing history. 
                            The bracket is divided into four quadrants, each containing 16 moments competing in a standard tournament format. 
                            Each quadrant will produce one winner, and those four winners will advance to the Final Four (Round 5). 
                            The Final Four winners will then compete in the Championship (Round 6) to determine the ultimate Top Moment.
                            <br/><br/>
                            For each matchup, click the moment you want to advance to the next round. Your pick should be highlighted in green. 
                            Do this for all six rounds. Click the info button next to each moment to read its description and learn more about why it's a Top Moment.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => {
                            handleHelpClose()
                        }} color="primary" autoFocus>
                            Got It
                        </Button>
                    </DialogActions>
                </Dialog>
                <Dialog
                    open={shareOpen}
                    onClose={handleShareClose}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title" variant={"h6"}
                                 align={"left"}>{"Share Your Bracket"}</DialogTitle>
                    <Divider/>
                    <DialogContent>
                        <Grid container sx={{
                            minWidth: 'min(350px, 60vw)',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: "row",
                            justifyContent: 'space-evenly',
                        }}>
                            <FacebookShareButton url={getShareLink()} hashtag={"#DRBB"}>
                                <Grid container>
                                    <FacebookIcon style={{color: '#1877f2', paddingRight: 2}}/>
                                    <Typography variant="body2" color="text.secondary" align="left">
                                        Facebook
                                    </Typography>
                                </Grid>
                            </FacebookShareButton>
                            <TwitterShareButton title={`View My Top Moments Bracket For ${props?.event.name}`} via={"dragracebb"}
                                                url={getShareLink()}
                                                hashtags={["DRBB", "NHRA", "DragRacing", "Bracket", "TopMoments"]}>
                                <Grid container>
                                    <TwitterIcon style={{color: '#1da1f2', paddingRight: 2}}/>
                                    <Typography variant="body2" color="text.secondary" align="left">
                                        Twitter
                                    </Typography>
                                </Grid>
                            </TwitterShareButton>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button color="primary" autoFocus onClick={handleShareClose}>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
                <Grid container sx={{
                    display: 'flex',
                    flexDirection: "column",
                    height: '100%',
                    maxWidth: '100vw',
                }}>
                    {!loading && props?.event !== undefined && league !== undefined &&
                        <Grid container sx={{
                            display: 'flex',
                            flexDirection: "column",
                            maxWidth: '100vw',
                            alignItems: 'center',
                            padding: '12px',
                            justifyContent: 'center',
                        }}>
                            <Grid container sx={{
                                backgroundColor: 'background.paper',
                                justifyContent: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                margin: 'auto',
                                width: 'fit-content',
                                alignItems: 'center',
                                padding: '8px',
                                borderRadius: '8px',
                            }}>
                                <Typography variant={"h4"} align={"center"} marginRight={'12px'} fontWeight={"bold"}>
                                    {`${nickname}'s Top Moments Bracket`}
                                </Typography>
                                <Typography variant={"subtitle2"} align={"left"}>
                                    {`(${props.event.name} - ${league.name})`}
                                </Typography>
                            </Grid>
                        </Grid>
                    }
                    {props?.event !== undefined && ownPicks &&
                        <Toolbar
                            component="nav"
                            variant="dense"
                            sx={{
                                justifyContent: 'space-between',
                                overflowX: 'auto',
                                maxWidth: '100vw',
                            }}
                        >
                            {!props.event.picks_locked &&
                                <Button
                                    variant="contained"
                                    endIcon={<PublishIcon/>}
                                    sx={{
                                        minWidth: '120px',
                                        backgroundColor: "#486ce7",
                                        color: "#ffffff",
                                        marginX: 1,
                                        ":hover": {
                                            backgroundColor: "#c40000",
                                            color: "#ffffff",
                                        }
                                    }}
                                    disabled={isClicked}
                                    onClick={() => {
                                        setIsClicked(true);
                                        handleSubmitPicks(true).then(() => {
                                            setIsClicked(false);
                                        });
                                    }}
                                >
                                    <Typography variant={"h6"} fontWeight={"bold"}>
                                        Submit
                                    </Typography>
                                </Button>
                            }
                            <Button
                                variant="contained"
                                endIcon={<IosShareIcon/>}
                                sx={{
                                    minWidth: '120px',
                                    backgroundColor: "#486ce7",
                                    color: "#ffffff",
                                    marginX: 1,
                                    ":hover": {
                                        backgroundColor: "#c40000",
                                        color: "#ffffff",
                                    }
                                }}
                                onClick={() => {
                                    handleShareClickOpen()
                                }}
                            >
                                <Typography variant={"h6"} fontWeight={"bold"}>
                                    Share
                                </Typography>
                            </Button>
                            {!props.event.picks_locked &&
                                <Button
                                    variant="contained"
                                    endIcon={<ShuffleIcon/>}
                                    sx={{
                                        minWidth: '150px',
                                        backgroundColor: "#486ce7",
                                        color: "#ffffff",
                                        marginX: 1,
                                        ":hover": {
                                            backgroundColor: "#c40000",
                                            color: "#ffffff",
                                        }
                                    }}
                                    onClick={() => {
                                        if (props?.event && user) {
                                            let randomizedPicks = randomizePicksTopMoments(generateBlankPicks({
                                                isFourWide: false,
                                                player: user,
                                                league: props.picks[0].league,
                                                results: props.results,
                                            }) as Pick[]);
                                            randomizedPicks.forEach((pick2: Pick) => {
                                                const result = props?.results?.find((r) => r.game_code === pick2.result.game_code);
                                                if(pick2.competitor_1){
                                                    // For Top Moments, use seed from result if available, otherwise look up from qualifiers
                                                    pick2.seed_1 = result?.seed_1 ?? props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_1})?.seed;
                                                }
                                                else{
                                                    pick2.seed_1 = undefined;
                                                }
                                                if(pick2.competitor_2){
                                                    // For Top Moments, use seed from result if available, otherwise look up from qualifiers
                                                    pick2.seed_2 = result?.seed_2 ?? props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.competitor_2})?.seed;
                                                }
                                                else{
                                                    pick2.seed_2 = undefined;
                                                }
                                                if(pick2.winner){
                                                    // Winner seed should match the seed of the competitor that won
                                                    if(pick2.competitor_1 === pick2.winner){
                                                        pick2.winner_seed = pick2.seed_1;
                                                    } else if(pick2.competitor_2 === pick2.winner){
                                                        pick2.winner_seed = pick2.seed_2;
                                                    } else {
                                                        pick2.winner_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.winner})?.seed;
                                                    }
                                                }
                                                else{
                                                    pick2.winner_seed = undefined;
                                                }
                                                if(pick2.loser){
                                                    // Loser seed should match the seed of the competitor that lost
                                                    if(pick2.competitor_1 === pick2.loser){
                                                        pick2.loser_seed = pick2.seed_1;
                                                    } else if(pick2.competitor_2 === pick2.loser){
                                                        pick2.loser_seed = pick2.seed_2;
                                                    } else {
                                                        pick2.loser_seed = props?.qualifiers.find((qualifier) => {return qualifier.driver === pick2.loser})?.seed;
                                                    }
                                                }
                                                else{
                                                    pick2.loser_seed = undefined;
                                                }
                                            });
                                            setProps({
                                                event: props.event,
                                                picks: randomizedPicks,
                                                brackets: props.brackets,
                                                results: props.results,
                                                qualifiers: props.qualifiers,
                                            });
                                            setChanged("changed");
                                        }
                                    }}
                                >
                                    <Typography variant={"h6"} fontWeight={"bold"}>
                                        Randomize
                                    </Typography>
                                </Button>
                            }
                            {!props.event.picks_locked &&
                                <Button
                                    variant="contained"
                                    endIcon={<RestartAltIcon/>}
                                    sx={{
                                        minWidth: '120px',
                                        backgroundColor: "#486ce7",
                                        color: "#ffffff",
                                        marginX: 1,
                                        ":hover": {
                                            backgroundColor: "#c40000",
                                            color: "#ffffff",
                                        }
                                    }}
                                    onClick={() => {
                                        handleResetClickOpen()
                                    }}
                                >
                                    <Typography variant={"h6"} fontWeight={"bold"}>
                                        Reset
                                    </Typography>
                                </Button>
                            }
                        </Toolbar>
                    }
                    {props?.picks !== undefined && props.picks.length > 0 ?
                        <Box sx={{
                            overflowX: 'auto',
                            display: 'flex',
                            justifyContent: 'center',
                            width: '100%',
                        }}>
                            <PickBracketContainerTopMoments
                                picks_locked={props?.event.picks_locked || !ownPicks}
                                brackets={props?.brackets}
                                picks={props?.picks}
                                results={props?.results}
                                updatePick={updatePick}
                                event={props?.event}
                                qualifiers={props?.qualifiers}
                            />
                        </Box>
                        :
                        <Grid container sx={{height: '50vh', width: '100%'}}>
                            {loading ? <Loader/> : <Typography variant={"h6"} align={"center"} margin={"auto"}>Picks are not available to be made at this time.</Typography>}
                        </Grid>
                    }
                </Grid>
            </Layout>
        </>
    );
}
